"use client";

import React, { ReactNode, useMemo } from "react";
import { useAppTheme } from "@shared/theme/ThemeProvider";
import { getTableTitleBackgroundWidth } from "@utils/tableTitleWidth";

interface Props {
    title?: string;
    autoHeight?: boolean;
    fullHeight?: boolean;
    children?: ReactNode;
    showCloseButton?: boolean;
    onClose?: () => void;
}

const TableContainer: React.FC<Props> = ({
    title,
    autoHeight,
    fullHeight,
    children,
    showCloseButton,
    onClose,
}) => {
    const currentTheme = useAppTheme();

    const paperBg = currentTheme.colors.paperBackground;
    const paperLine = currentTheme.colors.paperLineColor;
    const titleColor = currentTheme.colors.tableTitleTextColor;
    const titleFontSize = currentTheme.tableTitleFontSize;

    const titleWidth = useMemo(
        () =>
            getTableTitleBackgroundWidth(title, {
                fontSize: titleFontSize,
            }),
        [title, titleFontSize],
    );

    // RN: screenHeight - 300，Web 用 100vh 模擬
    const maxHeight = autoHeight || fullHeight ? "calc(100vh - 300px)" : "auto";
    const minHeight = fullHeight ? "calc(100vh - 300px)" : undefined;

    return (
        <div className="relative w-full">
            {/* 上框線 */}
            <div className="relative h-[17px]">
                {/* 左上角 corner_1 */}
                <div
                    className="absolute left-0 top-0 h-[17px] w-[17px] bg-[length:100%_100%] bg-no-repeat"
                    style={{ backgroundImage: 'url("/images/table_corner_1.png")' }}
                />
                {/* 右上角 corner_2 */}
                <div
                    className="absolute right-0 top-0 h-[17px] w-[17px] bg-[length:100%_100%] bg-no-repeat"
                    style={{ backgroundImage: 'url("/images/table_corner_2.png")' }}
                />

                {/* 中間橫線背景 */}
                <div
                    className="relative ml-[17px] flex h-[17px] w-[calc(100%-34px)] items-start"
                    style={{ backgroundColor: paperBg }}
                >
                    {/* 三段水平線（8px 高） */}
                    <div className="h-2 w-9" style={{ backgroundColor: paperLine }} />
                    <div
                        className="ml-[55px] h-2 w-[calc(100%-183px)]"
                        style={{ backgroundColor: paperLine }}
                    />
                    <div
                        className="ml-[55px] h-2 w-[37px]"
                        style={{ backgroundColor: paperLine }}
                    />

                    {/* 下緣的斜切小三角形們 */}
                    <div
                        className="absolute bottom-2 left-9 h-[9px] w-[9px]"
                        style={{
                            background: `linear-gradient(135deg, ${paperLine} 50%, ${paperBg} 50%)`,
                        }}
                    />
                    <div
                        className="absolute bottom-[9px] left-[83px] h-[9px] w-[9px]"
                        style={{
                            background: `linear-gradient(45deg, ${paperBg} 50%, ${paperLine} 50%)`,
                        }}
                    />
                    <div
                        className="absolute bottom-2 right-[83px] h-[9px] w-[9px]"
                        style={{
                            background: `linear-gradient(135deg, ${paperLine} 50%, ${paperBg} 50%)`,
                        }}
                    />
                    <div
                        className="absolute bottom-[9px] right-9 h-[9px] w-[9px]"
                        style={{
                            background: `linear-gradient(45deg, ${paperBg} 50%, ${paperLine} 50%)`,
                        }}
                    />
                </div>

                {/* 左上斜切角塊 */}
                <div
                    className="absolute left-[9px] top-2 h-[9px] w-[9px]"
                    style={{
                        background: `linear-gradient(135deg, ${paperLine} 50%, ${paperBg} 50%)`,
                    }}
                />
                {/* 右上斜切角塊 */}
                <div
                    className="absolute right-[9px] top-2 h-[9px] w-[9px]"
                    style={{
                        background: `linear-gradient(45deg, ${paperBg} 50%, ${paperLine} 50%)`,
                    }}
                />
            </div>

            {/* 上方裝飾 (table_top_image 左右各一塊) */}
            <div className="absolute left-0 top-0 -mt-[7px] h-[7px] w-full">
                <div
                    className="absolute left-[50px] top-0 h-3 w-[62px] bg-[length:100%_100%] bg-no-repeat"
                    style={{ backgroundImage: 'url("/images/table_top_image.png")' }}
                />
                <div
                    className="absolute right-[50px] top-0 h-3 w-[62px] bg-[length:100%_100%] bg-no-repeat"
                    style={{ backgroundImage: 'url("/images/table_top_image.png")' }}
                />
            </div>

            {/* 中間主體 */}
            <div className="relative w-full pb-4" style={{ backgroundColor: paperBg }}>
                {/* 四邊斜切角（外框） */}
                <div
                    className="absolute left-0 top-[41px] h-[10px] w-[10px]"
                    style={{
                        background: `linear-gradient(45deg, ${paperLine} 50%, ${paperBg} 50%)`,
                    }}
                />
                <div
                    className="absolute bottom-[22px] left-0 h-[10px] w-[10px]"
                    style={{
                        background: `linear-gradient(135deg, ${paperLine} 50%, ${paperBg} 50%)`,
                    }}
                />
                <div
                    className="absolute right-0 top-10 h-[10px] w-[10px]"
                    style={{
                        background: `linear-gradient(135deg, ${paperBg} 50%, ${paperLine} 50%)`,
                    }}
                />
                <div
                    className="absolute bottom-[22px] right-0 h-[10px] w-[10px]"
                    style={{
                        background: `linear-gradient(45deg, ${paperBg} 50%, ${paperLine} 50%)`,
                    }}
                />

                {/* 標題列 + 關閉按鈕 */}
                <div
                    className="relative h-[50px] border-r-[3px]"
                    style={{ borderRightColor: paperLine }}
                >
                    {title && (
                        <div className="flex items-start pl-6 pt-0.5">
                            <div
                                className="flex h-[30px] shrink-0 items-center bg-[length:100%_100%] bg-no-repeat pl-2"
                                style={{
                                    width: `${titleWidth}px`,
                                    backgroundImage: 'url("/images/table_title.png")',
                                }}
                            >
                                <span
                                    className="shrink-0 whitespace-nowrap py-0 font-bold"
                                    style={{ fontSize: titleFontSize, color: titleColor }}
                                >
                                    [ {title} ]
                                </span>
                            </div>
                        </div>
                    )}

                    {showCloseButton && (
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="關閉"
                            className="absolute right-5 top-3 h-7 w-[29px] shrink-0 overflow-hidden rounded-[2px] border-0 bg-[#1F7F8E] p-0 hover:bg-[#2492a4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        >
                            <span
                                className="mx-auto block h-[25px] w-[25px] bg-[length:100%_100%] bg-no-repeat"
                                style={{ backgroundImage: 'url("/images/cross.png")' }}
                                aria-hidden
                            />
                        </button>
                    )}
                </div>

                {/* 內容 + 左右粗線 + 捲動 */}
                <div
                    className="mb-4 border-l-[10px] border-r-[10px] px-4 pt-1"
                    style={{
                        borderLeftColor: paperLine,
                        borderRightColor: paperLine,
                        minHeight,
                        maxHeight,
                        overflowY: maxHeight === "auto" ? "visible" : "auto",
                    }}
                >
                    {children}
                </div>
            </div>

            {/* 下框線 */}
            <div className="relative mb-2.5 h-[17px]">
                <div
                    className="absolute bottom-0 left-0 h-[17px] w-[17px] bg-[length:100%_100%] bg-no-repeat"
                    style={{ backgroundImage: 'url("/images/table_corner_3.png")' }}
                />
                <div
                    className="absolute bottom-0 right-0 h-[17px] w-[17px] bg-[length:100%_100%] bg-no-repeat"
                    style={{ backgroundImage: 'url("/images/table_corner_4.png")' }}
                />

                <div
                    className="ml-[17px] flex h-[17px] w-[calc(100%-34px)] items-end"
                    style={{ backgroundColor: paperBg }}
                >
                    <div className="h-2 w-full" style={{ backgroundColor: paperLine }} />
                </div>

                <div
                    className="absolute bottom-2 left-2 h-[10px] w-[10px]"
                    style={{
                        background: `linear-gradient(45deg, ${paperLine} 50%, ${paperBg} 50%)`,
                    }}
                />
                <div
                    className="absolute bottom-[7px] right-[9px] h-[10px] w-[10px]"
                    style={{
                        background: `linear-gradient(135deg, ${paperBg} 50%, ${paperLine} 50%)`,
                    }}
                />
            </div>
        </div>
    );
};

export default TableContainer;
